import { addDays, daysBetween, fmtDate, todayStr } from "./format";

/**
 * SIT Local Confirmed (long distance) — design of 2026-10-24, Asana 1219846172081510 /
 * 1219846172081517 / 1219846172081526.
 *
 * After the shipment is at our hub the MC agrees a local delivery date with
 * Operations and the customer. Operations usually assigns the crew later, so the
 * MC checks in with Operations 2 days before that date. The fields live in the
 * job's s2Notes: sitOpsDate (given by Operations), sitConfirmedDate (confirmed
 * with the customer), sitOpsCheckDate, sitCheckDoneAt, sitCrewOk ("yes" | "no" | ""),
 * sitCrewConfirmedAt, sitCrewNote.
 */

/** The check-in is this many days before the delivery date the MC confirmed. */
export const OPS_CHECK_DAYS_BEFORE = 2;

const notesOf = (job) => (job && job.s2Notes && !Array.isArray(job.s2Notes) ? job.s2Notes : {});

/** "Delivery date given by Operations" (the older "SIT Local delivery date" field counts). */
export const sitOpsDate = (job) => {
  const n = notesOf(job);
  return n.sitOpsDate || n.sitDeliveryDate || "";
};

/** "Delivery date confirmed by the move coordinator". */
export const sitConfirmedDate = (job) => notesOf(job).sitConfirmedDate || "";

/** 2 days before the confirmed delivery date, never in the past. */
export const defaultCheckDate = (confirmedDate, today = todayStr()) => {
  if (!confirmedDate) return "";
  const check = addDays(confirmedDate, -OPS_CHECK_DAYS_BEFORE);
  return check < today ? today : check;
};

/** The check-in date: the one the MC set, else the rule. */
export const sitCheckDate = (job, today = todayStr()) =>
  notesOf(job).sitOpsCheckDate || defaultCheckDate(sitConfirmedDate(job), today);

/** A crew is on the delivery: Operations told the MC so, or dispatch put one on the job in the planner. */
export const crewAssigned = (job) => notesOf(job).sitCrewOk === "yes" || !!(job && job.localCrewAssignedAt);

/**
 * The tag on the SIT Local Confirmed card (Miles, 2026-10-24):
 *  - "Crew assigned" once a crew is on the delivery;
 *  - "Crew needed — check today" with no crew 1 day (or less) before the delivery date;
 *  - "Crew to be assigned" with no crew 2 days or more before it.
 * cls: ok | warn | wait, for the colour.
 */
export const crewTag = (job, today = todayStr()) => {
  if (crewAssigned(job)) return { cls: "ok", label: "Crew assigned" };
  const delivery = sitConfirmedDate(job);
  if (delivery && daysBetween(today, delivery) <= 1) {
    return { cls: "warn", label: "Crew needed — check today" };
  }
  return { cls: "wait", label: "Crew to be assigned" };
};

/** The Operations check-in line: due today, in N days, overdue, or done. null with no date to go by. */
export const sitCheckInfo = (job, today = todayStr()) => {
  const check = sitCheckDate(job, today);
  if (!check) return null;
  if (crewAssigned(job)) {
    return {
      cls: "ok",
      label: notesOf(job).sitCrewOk === "yes" ? "Crew confirmed by Operations" : "Crew assigned by dispatch",
    };
  }
  if (notesOf(job).sitCheckDoneAt) return { cls: "warn", label: "Check-in done — crew not confirmed yet" };
  const d = daysBetween(today, check);
  if (d < 0) return { cls: "bad", label: `Ops check-in overdue by ${-d} day${-d > 1 ? "s" : ""}` };
  if (d === 0) return { cls: "warn", label: "Ops check-in due today" };
  return { cls: "wait", label: `Ops check-in in ${d} day${d > 1 ? "s" : ""} (${fmtDate(check)})` };
};

/** Most urgent first in the SIT Local Confirmed column: overdue, due today, waiting, crew assigned. */
const RANK = { bad: 0, warn: 1, wait: 2, ok: 3 };
export const bySitUrgency = (jobs, today = todayStr()) =>
  jobs.slice().sort((a, b) => {
    const ca = sitCheckInfo(a, today);
    const cb = sitCheckInfo(b, today);
    const ra = ca ? RANK[ca.cls] : 2;
    const rb = cb ? RANK[cb.cls] : 2;
    return ra - rb || String(sitCheckDate(a, today)).localeCompare(String(sitCheckDate(b, today)));
  });

/** "Dates match" / "Dates don't match" / waiting, under the two date panels. */
export const sitDatesState = (job) => {
  const ops = sitOpsDate(job);
  const conf = sitConfirmedDate(job);
  if (ops && conf && ops === conf) return { cls: "ok", text: `Dates match — delivery confirmed for ${fmtDate(conf)}` };
  if (ops && conf) {
    return {
      cls: "bad",
      text: `Dates don't match — Operations ${fmtDate(ops)} vs. customer ${fmtDate(conf)}. Resolve with Operations.`,
    };
  }
  if (!ops) return { cls: "wait", text: "Waiting for the date from Operations" };
  return { cls: "wait", text: "Waiting to confirm the date with the customer" };
};

/** Why "SIT Local Confirmed" cannot be pressed yet, or "" when it can. */
export const confirmSitError = (job, today = todayStr()) => {
  const conf = sitConfirmedDate(job);
  const ops = sitOpsDate(job);
  if (!conf) return "Add the delivery date confirmed with the customer before confirming SIT Local.";
  if (ops && ops !== conf) {
    return `The dates don't match — Operations ${fmtDate(ops)} vs. customer ${fmtDate(conf)}. Resolve with Operations first.`;
  }
  if (conf < today) return "The confirmed delivery date is in the past.";
  return "";
};

/** Why "Move to Delivery Day in Progress" cannot be pressed yet, or "". */
export const moveToDeliveryDayError = (job) => {
  if (!job || job.status !== "ld-sit-confirmed") return "Confirm SIT Local first.";
  if (!crewAssigned(job)) return "Confirm with Operations that a crew is assigned before moving to Delivery Day.";
  return "";
};

// ---- Delivery Day: Mark Completed (Asana 1219141409140286) ----------------------------

/** The day the delivery is (or was) due: local delivery in Zoho, the driver's ETA, else the date the MC confirmed. */
export const deliveryDueDate = (job) =>
  (job && job.serviceDates && job.serviceDates.localDeliveryDate) ||
  (job && job.driverDelivery && job.driverDelivery.etaDate) ||
  sitConfirmedDate(job) ||
  "";

/**
 * Why Mark Completed is still locked, or "" when the MC may press it. The control
 * Miles asked for, instead of a second "are you sure" click: the system must see
 * the delivery happened (delivery BOL signed, the driver delivered, or the delivery
 * date has come) and the MC must have written the client's feedback.
 */
export const deliveryCompleteBlocker = (job, feedback, today = todayStr()) => {
  const due = deliveryDueDate(job);
  const happened =
    !!(job && job.bolDeliverySignedAt) || !!(job && job.driverDelivered) || (!!due && due <= today);
  if (!happened) {
    return due
      ? `Unlocks on the delivery date (${fmtDate(due)}) or once the delivery BOL is signed.`
      : "Unlocks once the delivery BOL is signed or a delivery date is set.";
  }
  if (!String(feedback || "").trim()) return "Write the client's feedback first.";
  return "";
};

/** The Post Move queue a completed delivery goes to (mirrors McOpsPostMoveRouting::status). */
export const postMoveStatusFor = (job) => (job && job.finishRoute === "auto" ? "post-move-auto" : "post-move-vip");
